import type { FC } from 'react';
import React, { useMemo, useState } from 'react';

import { ChevronDownIcon, PencilIcon, TrashBinIcon } from '../../../assets/icons/icons';
import { useRegions } from '../../../hooks/useRegions';
import type { AnalogsDelivery, Position } from '../../../types/project';
import { MAX_QTY } from '../../../data/projectCard';
import { cx } from '../../../utils/cx';
import { formatMoney, formatNumber, formatRub } from '../../../utils/format';
import { isSearchStarted } from '../../../entities/project/model/position';
import styles from './PositionRow.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { Select } from '../../ui/Select/Select';
import type { AnalogsLogistics } from '../PositionDetails/PositionDetails';
import {
    formatRequestPrice,
    parseRequestPrice,
    sanitizeRequestPriceInput,
} from '../../../utils/requestPrice';
import {
    PRICE_MAX_INT_DIGITS,
    countBeforeCaret,
    countCharsBeforeCaret,
    groupNumbersInText,
    groupPrice,
    restoreCaret,
    restoreCaretByChars,
    stripSpaces,
} from '../../../utils/priceInput';

interface PositionRowProps {
    dirty?: boolean;
    onRestart?: () => void;
    onDiscard?: () => void;
    onDimensions?: () => void;
    position: Position;
    index: number;
    projectRegion: string;
    duplicateOf?: number;
    open: boolean;
    onOpen: () => void;
    onToggle: (key: 'analogs' | 'logistics') => void;
    onChange: (patch: Partial<Position>) => void;
    onEdit: () => void;
    onDelete: () => void;
    analogsLogistics?: AnalogsLogistics;
    showRestart?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
    initial: 'в очереди',
    parsing: 'разбираем запрос',
    processing: 'идёт поиск',
    searching: 'идёт поиск',
    finished: 'предложений не нашлось',
    canceled: 'поиск отменён',
    error: 'ошибка поиска',
    failed: 'ошибка поиска',
};

const DONE = new Set(['finished', 'canceled', 'error', 'failed']);

const INTERACTIVE =
    'input, button, select, textarea, a, label, [role="listbox"], [role="option"], [role="combobox"]';

const ANALOGS_DIMS_VISIBLE = new Set<AnalogsDelivery>(['processing', 'partial', 'calculated']);

const priceLabel = (from: number, to: number): string => {
    if (!from && !to) return '';
    if (from && to) return `${formatNumber(from)} — ${formatNumber(to)}`;
    if (to) return `до ${formatNumber(to)}`;
    return `от ${formatNumber(from)}`;
};

const parsePrice = (value: string): { priceFrom: number; priceTo: number } => {
    const numbers = value
        .replace(/[\u00a0\u202f]/g, '')
        .match(/\d+/g)
        ?.map((digits) => Number(digits.slice(0, PRICE_MAX_INT_DIGITS))) ?? [];

    if (!numbers.length) return { priceFrom: 0, priceTo: 0 };
    if (numbers.length === 1) {
        return /до/i.test(value)
            ? { priceFrom: 0, priceTo: numbers[0] }
            : { priceFrom: numbers[0], priceTo: 0 };
    }

    return { priceFrom: Math.min(...numbers), priceTo: Math.max(...numbers) };
};

export const PositionRow: FC<PositionRowProps> = ({
    position,
    index,
    projectRegion,
    duplicateOf = 0,
    open,
    onOpen,
    dirty = false,
    onRestart,
    onDiscard,
    onDimensions,
    onToggle,
    onChange,
    onEdit,
    onDelete,
    analogsLogistics,
    showRestart = true,
}) => {
    const regions = useRegions(position.region || projectRegion);
    const inherited = !position.region || position.region === projectRegion;

    const offersCount = position.offers?.length ?? position.offersCount ?? 0;

    const analogsDelivery: AnalogsDelivery =
        analogsLogistics === 'processing' ? 'processing' : position.analogsDelivery;

    const analogsDimsOn = ANALOGS_DIMS_VISIBLE.has(analogsDelivery);

    const showDims = (position.logistics || analogsDimsOn) && !position.nameInvalid;

    const dimsPending = position.logistics
        ? Boolean(position.deliveryProcessing)
        : analogsDelivery === 'processing';

    const searching = Boolean(position.searchStatus) && !DONE.has(position.searchStatus ?? '');
    const searchStarted = isSearchStarted(position);
    const showResults = !searching && searchStarted;
    const canExpand = showResults;

    const [regionListOpen, setRegionListOpen] = useState(false);
    const [typing, setTyping] = useState<{ qty: boolean; requestPrice: boolean; price: boolean }>({
        qty: false,
        requestPrice: false,
        price: false,
    });

    const markTyping = (field: 'qty' | 'requestPrice' | 'price', changed: boolean) =>
        setTyping((prev) => (prev[field] === changed ? prev : { ...prev, [field]: changed }));

    const showDirty = dirty || typing.qty || typing.requestPrice || typing.price;

    const regionOptions = useMemo(
        () => regions.map((item) => ({ value: item, label: item })),
        [regions],
    );

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (!canExpand) return;
        if ((event.target as HTMLElement).closest(INTERACTIVE)) return;
        /* Пользователь выделял текст (например, наименование) — это не клик. */
        if (window.getSelection()?.toString()) return;
        onOpen();
    };

    const best = position.offers?.length
        ? Math.min(...position.offers.map((offer) => offer.price))
        : position.minFoundPrice ?? 0;

    const requestPrice = position.requestPrice ?? null;

    // let state: React.ReactNode = <span className={styles.dash}>не запускалась</span>;
    let state: React.ReactNode = <span className={styles.dash}>поиск не запускался</span>;

    if (!searching && offersCount) {
        state = (
            <span className={styles.money}>
                <b>{formatRub(best)}</b>
                <small>
                    {offersCount} предложени{offersCount === 1 ? 'е' : offersCount < 5 ? 'я' : 'й'}
                </small>
            </span>
        );
    } else if (position.isDuplicate) {
        state = (
            <span
                className={styles.dash}
            >
                {duplicateOf ? `дубликат позиции ${duplicateOf}` : 'дубликат'}
            </span>
        );
    } else if (position.nameInvalid) {
        state = (
            <span
                className={styles.invalid}
            >
                некорректный товар
            </span>
        );
    } else if (searching) {
        state = (
            <span className={styles.wait}>
                <span className={styles.spinner} />
                {STATUS_LABEL[position.searchStatus ?? ''] ?? 'идёт поиск'}
            </span>
        );
    } else if (position.searchStatus) {
        state = (
            <span className={styles.dash}>
                {STATUS_LABEL[position.searchStatus] ?? 'обрабатывается'}
            </span>
        );
    }

    return (
        <tr className={cx(styles.row, canExpand && styles.rowExpandable)} onClick={handleRowClick}>
            <td className={styles.idx}>{index}</td>

            <td className={styles.name}>
                <b>{position.name}</b>
                {position.spec && <small>{position.spec}</small>}
            </td>

            <td>
                <input
                    className={cx(styles.cell, styles.num)}
                    aria-label="Количество"
                    inputMode="numeric"
                    maxLength={String(MAX_QTY).length}
                    key={`qty-${position.qty}`}
                    defaultValue={position.qty}
                    disabled={searching}
                    onInput={(event) => {
                        const input = event.currentTarget;
                        input.value = input.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');

                        const next = Math.min(Number(input.value) || 1, MAX_QTY);
                        markTyping('qty', next !== position.qty);
                    }}
                    onBlur={(event) => {
                        markTyping('qty', false);
                        const typed = Number(event.target.value.replace(/\D/g, '')) || 1;
                        const qty = Math.min(typed, MAX_QTY);
                        event.target.value = String(qty);
                        if (qty !== position.qty) onChange({ qty });
                    }}
                />
            </td>

            <td>
                <Tooltip text={regionListOpen ? undefined : (position.region || projectRegion)}>
                    <Select
                        className={cx(styles.cellSelect, inherited && styles.inherit)}
                        options={regionOptions}
                        value={position.region || projectRegion}
                        disabled={searching}
                        onChange={(region) => onChange({ region })}
                        onOpenChange={setRegionListOpen}
                        aria-label="Регион поиска"
                    />
                </Tooltip>
            </td>

            {/* Цена товара за ед. из заявки (request_price) */}
            <td>
                <Tooltip
                    text={
                        requestPrice != null
                            ? `Цена из заявки: ${groupPrice(formatRequestPrice(requestPrice))} ₽ за ед.`
                            : 'Цена товара за ед. в заявке не указана'
                    }
                >
                    <input
                        className={cx(styles.cell, styles.num)}
                        aria-label="Цена товара за единицу"
                        placeholder="—"
                        inputMode="decimal"
                        key={`request-price-${requestPrice ?? 'none'}`}
                        defaultValue={groupPrice(formatRequestPrice(requestPrice))}
                        disabled={searching}
                        onInput={(event) => {
                            const input = event.currentTarget;
                            const before = countBeforeCaret(input);
                            const next = groupPrice(sanitizeRequestPriceInput(stripSpaces(input.value)));

                            if (next !== input.value) {
                                input.value = next;
                                restoreCaret(input, before);
                            }

                            markTyping('requestPrice', parseRequestPrice(stripSpaces(next)) !== requestPrice);
                        }}
                        onBlur={(event) => {
                            markTyping('requestPrice', false);
                            const next = parseRequestPrice(stripSpaces(event.target.value));
                            event.target.value = groupPrice(formatRequestPrice(next));
                            if (next !== requestPrice) onChange({ requestPrice: next });
                        }}
                    />
                </Tooltip>
            </td>

            <td>
                <Tooltip text={priceLabel(position.priceFrom, position.priceTo) || 'Ограничение по цене не задано'}>
                    <input
                        className={cx(styles.cell, styles.num)}
                        aria-label="Ограничение цены"
                        placeholder="—"
                        key={`price-${position.priceFrom}-${position.priceTo}`}
                        defaultValue={priceLabel(position.priceFrom, position.priceTo)}
                        disabled={searching}
                        onInput={(event) => {
                            const input = event.currentTarget;
                            const before = countCharsBeforeCaret(input);
                            const next = groupNumbersInText(input.value);

                            if (next !== input.value) {
                                input.value = next;
                                restoreCaretByChars(input, before);
                            }

                            const parsed = parsePrice(next);
                            markTyping(
                                'price',
                                parsed.priceFrom !== position.priceFrom || parsed.priceTo !== position.priceTo,
                            );
                        }}
                        onBlur={(event) => {
                            markTyping('price', false);
                            const next = parsePrice(event.target.value);
                            event.target.value = priceLabel(next.priceFrom, next.priceTo);
                            if (next.priceFrom !== position.priceFrom || next.priceTo !== position.priceTo) {
                                onChange(next);
                            }
                        }}
                    />
                </Tooltip>
            </td>

            <td className={styles.center}>
                <Tooltip text="Искать аналоги товара" align="right">
                    <button
                        type="button"
                        className={styles.sw}
                        aria-label="Искать аналоги товара"
                        aria-pressed={position.analogs}
                        disabled={searching}
                        onClick={() => onToggle('analogs')}
                    />
                </Tooltip>
            </td>

            <td className={styles.center}>
                <Tooltip text="Рассчитать доставку до адреса проекта" align="right">
                    <button
                        type="button"
                        className={styles.sw}
                        aria-label="Рассчитать доставку"
                        aria-pressed={position.logistics}
                        disabled={searching}
                        onClick={() => onToggle('logistics')}
                    />
                </Tooltip>
            </td>

            <td className={styles.state}>{state}</td>

            <td className={styles.state}>
                {showResults && position.avgFoundPrice ? (
                    <span className={cx(styles.money, styles.avg)}>
                        <b>{formatMoney(position.avgFoundPrice)} ₽</b>
                        <small>за единицу</small>
                    </span>
                ) : (
                    <span className={styles.dash}>—</span>
                )}
            </td>

            <td className={styles.dimsCell}>
                {showDims ? (
                    <Tooltip
                        text={dimsPending ? 'Считаем габариты и доставку' : 'Информация по габаритам'}
                        align="right"
                    >
                        <button
                            type="button"
                            className={styles.dims}
                            aria-label="Информация по габаритам"
                            disabled={dimsPending}
                            onClick={onDimensions}
                        >
                            <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                                <path
                                    d="M10 2.6l6.1 3.5v7.8L10 17.4 3.9 13.9V6.1L10 2.6z"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M3.9 6.1L10 9.7l6.1-3.6M10 9.7v7.7"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </Tooltip>
                ) : (
                    <span className={styles.dimsPlaceholder} aria-hidden="true" />
                )}
            </td>

            <td>
                {canExpand && (
                    <button
                        type="button"
                        className={cx(styles.expand, open && styles.expandOpen)}
                        aria-label={open ? 'Свернуть' : 'Показать подробности'}
                        aria-expanded={open}
                        onClick={onOpen}
                    >
                        <ChevronDownIcon />
                    </button>
                )}
            </td>

            <td>
                <div className={cx(styles.tools, showDirty && showRestart && styles.toolsDirty)}>
                    {showDirty && showRestart && onRestart && !searching && (
                        <span className={styles.restartWide}>
                            <Tooltip text="Позиция изменена — перезапустить поиск" align="right">
                                <button
                                    type="button"
                                    className={styles.restartText}
                                    onClick={onRestart}
                                >
                                    Перезапустить поиск
                                </button>
                            </Tooltip>

                            <Tooltip text="Отменить изменения" align="right">
                                <button
                                    type="button"
                                    className={styles.restartCancel}
                                    aria-label="Отменить изменения"
                                    onClick={onDiscard}
                                >
                                    <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true">
                                        <path
                                            d="M5 5l10 10M15 5L5 15"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </button>
                            </Tooltip>
                        </span>
                    )}

                    {!searching && (
                        <div className={styles.actions}>
                            <Tooltip text="Изменить наименование и характеристики" align="right">
                                <button
                                    type="button"
                                    className={styles.iconBtn}
                                    aria-label="Изменить позицию"
                                    onClick={onEdit}
                                >
                                    <PencilIcon />
                                </button>
                            </Tooltip>
                            <Tooltip text="Удалить позицию" align="right">
                                <button
                                    type="button"
                                    className={cx(styles.iconBtn, styles.danger)}
                                    aria-label="Удалить позицию"
                                    onClick={onDelete}
                                >
                                    <TrashBinIcon />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};